import { useCallback, useMemo } from 'react';
import { ApolloError } from '@apollo/client';

import { useErpSurveyResponseSearchQuery } from 'core/graphql';

import { ISurveyAnalyticsFilter } from './analyticsModel';

/**
 * ТЗ 0124 — «Все ответы» и поиск студента: отправленные ответы строкой на
 * ответ (`surveyResponses`), фильтры и страницы — на сервере.
 */

export interface ISurveyResponseRow {
  responseId: string;
  submittedAt: string;
  studentId: string;
  studentName: string;
  groupId?: string | null;
  groupName?: string | null;
  courseId?: string | null;
  courseName?: string | null;
  surveyId: string;
  surveyTitle: string;
  assignmentId: string;
  lessonNumber?: number | null;
  lessonTitle?: string | null;
  /** Шкала 0–100 (индекс), `null` — в ответе нет оценочных вопросов. */
  avgScore?: number | null;
  commentsCount: number;
  hasProblem: boolean;
}

export interface ISurveyResponseSearchFilter {
  courseId?: string;
  groupId?: string;
  studentId?: string;
  surveyId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export const SURVEY_RESPONSES_PAGE_SIZE = 50;

const single = (values?: string[]): string | undefined =>
  values && values.length === 1 ? values[0] : undefined;

/**
 * Общий фильтр хаба (списки, как у аналитики) → фильтр `surveyResponses`
 * (по одному значению). `ignored` — что из общего фильтра к таблице ответов
 * не применилось: несколько курсов/групп/опросов, преподаватель, тема.
 * Экран говорит об этом вслух, а не показывает «лишние» строки молча.
 */
export const responseFilterFromAnalytics = (
  filter: ISurveyAnalyticsFilter,
  search?: string,
): { filter: ISurveyResponseSearchFilter; ignored: boolean } => {
  const many = (values?: string[]) => Boolean(values && values.length > 1);
  return {
    filter: {
      courseId: single(filter.courseIds),
      groupId: single(filter.groupIds),
      surveyId: single(filter.surveyIds),
      dateFrom: filter.dateFrom || undefined,
      dateTo: filter.dateTo || undefined,
      search: search?.trim() || undefined,
    },
    ignored:
      many(filter.courseIds) ||
      many(filter.groupIds) ||
      many(filter.surveyIds) ||
      Boolean(filter.teacherIds?.length) ||
      Boolean(filter.metrics?.length),
  };
};

const EMPTY: ISurveyResponseRow[] = [];

export const useSurveyResponseSearch = (
  filter: ISurveyResponseSearchFilter,
  {
    offset = 0,
    limit = SURVEY_RESPONSES_PAGE_SIZE,
    skip = false,
    keepPreviousPage = false,
  }: {
    offset?: number;
    limit?: number;
    skip?: boolean;
    /**
     * Перепроверка 0124: пока грузится следующая страница (или новый
     * фильтр), прежняя остаётся на экране — таблица приглушает её, а не
     * мигает спиннером и не прячет листалку.
     */
    keepPreviousPage?: boolean;
  } = {},
): {
  rows: ISurveyResponseRow[];
  total: number;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
} => {
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useErpSurveyResponseSearchQuery({
    variables: { filter, offset, limit },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const shown = data ?? (keepPreviousPage ? previousData : undefined);
  const rows = useMemo(
    () =>
      (shown?.surveyResponses.items as ISurveyResponseRow[] | undefined) ??
      EMPTY,
    [shown],
  );
  return {
    rows,
    total: shown?.surveyResponses.total ?? 0,
    loading,
    error,
    refetch: useCallback(() => {
      refetch().catch(() => undefined);
    }, [refetch]),
  };
};
